import { IconClock16 } from '@warp-ds/icons/react/clock-16';
import { IconDiner16 } from '@warp-ds/icons/react/diner-16';
import { IconHouseBed16 } from '@warp-ds/icons/react/house-bed-16';
import { IconPlaneLand16 } from '@warp-ds/icons/react/plane-land-16';
import { IconPlaneTakeOff16 } from '@warp-ds/icons/react/plane-take-off-16';
import { IconTag16 } from '@warp-ds/icons/react/tag-16';
import { IconUsers16 } from '@warp-ds/icons/react/users-16';
import { Alert } from '@warp-ds/react/components/alert';
import { Button } from '@warp-ds/react/components/button';
import { Expandable } from '@warp-ds/react/components/expandable';
import { useMemo } from 'react';

import { useConfig } from 'shared/config/ConfigContext';
import { formatReadableDateFull, formatReadableDayTime } from 'shared/util/datetime-fns';
import { formatDuration, thousandSeparated } from 'shared/util/formatting';
import type { HotelOffer, HotelResponse } from '@/interfaces';
import { buildFlightsResult, getNumberOfPax, getPricePerPerson, getPriceTotal } from '@/lib/utils/hotelData';
import { trackClickOfferHotel } from '@/lib/utils/tracking';
import displayValues from '@/resources/displayValues.json';
import logos from './airlineLogos';
import { HotelResultHeader } from './HotelResultHeader';

const meals: Record<string, string> = displayValues.amenities.meals;

export type HotelResultItemProps = {
    offer: HotelOffer;
    hotel: HotelResponse;
    index: number;
    filterNumberOfAdults?: number;
    filterAgesOfChildren?: number[];
    paxEnabled: boolean;
};

export const HotelResultItem = ({ offer, hotel, index, filterNumberOfAdults, filterAgesOfChildren, paxEnabled }: HotelResultItemProps) => {
    const { contextPathPP } = useConfig();
    const { board, roomDescription, numberOfAdults, numberOfChildren, offerId, pricePerPerson, priceTotal } = offer;
    const { legsWithRows, showUnknownStopoversWarning, hasFlightDetails } = useMemo(() => buildFlightsResult(offer), [offer]);

    const headingLabel = index === 0 ? 'Beste tilbud' : index === 1 ? 'Flere tilbud' : null;
    const numPax = getNumberOfPax(numberOfAdults, numberOfChildren);
    const paxText = numPax === 1 ? `${numPax} person` : `${numPax} personer`;

    const adjustedPricePerPerson = getPricePerPerson(pricePerPerson, numPax, paxEnabled, priceTotal);
    const adjustedPriceTotal = getPriceTotal(pricePerPerson, numPax, paxEnabled, priceTotal);

    return (
        <article>
            {headingLabel && <h2 className="t3">{headingLabel}</h2>}

            <div className="border rounded-8">
                <Expandable
                    expanded={index === 0}
                    title={<HotelResultHeader {...offer} pricePerPerson={adjustedPricePerPerson} />}
                    buttonClass="p-16 flex flex-row justify-between w-full"
                    contentClass="p-16"
                    data-testid={`hotelResultExpansionPanel-${offer.offerId}`}>
                    <div className="flex flex-col md:basis-2/3 md:flex-row md:justify-between pt-8">
                        <section className="w-full">
                            <h3 className="sr-only md:not-sr-only md:mb-16!">Flyvning</h3>
                            <ul className="flex flex-col gap-8 justify-between ">
                                {legsWithRows.map(({ key, isReturn, headingDate, rows }) => (
                                    <li key={key}>
                                        <h4 className="flex flex-col items-start md:flex-row gap-8 md:items-center text-m t5">
                                            <span className="flex flex-row-reverse md:flex-row gap-8 items-center text-m">
                                                {isReturn ? <IconPlaneLand16 aria-hidden /> : <IconPlaneTakeOff16 aria-hidden />}
                                                <span className="flex gap-0">
                                                    {isReturn ? 'Hjemreise' : 'Utreise'} <span className="hidden md:inline">:</span>
                                                </span>
                                            </span>
                                            <time className="capitalize font-normal md:font-bold" dateTime={headingDate.toISOString()}>
                                                {formatReadableDateFull(headingDate)}
                                            </time>
                                        </h4>

                                        <ul className="flex flex-col gap-8 md:ml-8 md:border-l md:pt-8 md:pl-16">
                                            {rows.map(
                                                ({
                                                    flight,
                                                    index,
                                                    parsedDepartureTime,
                                                    parsedArrivalTime,
                                                    showStopoverAlert,
                                                    stopoverText,
                                                }) => (
                                                    <li key={`${flight.originAirport}-${flight.destinationAirport}-${index}`}>
                                                        <Alert type="info" show={!!showStopoverAlert} className="mb-16 mr-16">
                                                            {stopoverText}
                                                        </Alert>

                                                        {hasFlightDetails && flight.airlineCode && flight.flightNo && (
                                                            <div className="flex flex-row items-center gap-8 pb-8 text-s">
                                                                <img
                                                                    src={logos()[flight.airlineCode?.toLowerCase()]?.src}
                                                                    height={24}
                                                                    width={24}
                                                                    loading="lazy"
                                                                    alt=""
                                                                />
                                                                <span>
                                                                    {flight.airlineCode}
                                                                    {flight.flightNo}
                                                                </span>
                                                                <span>{flight.airline}</span>
                                                            </div>
                                                        )}
                                                        <p className="flex flex-row gap-4 md:gap-8 text-s">
                                                            <time dateTime={flight.departureTime} className="font-bold capitalize">
                                                                {parsedDepartureTime ? formatReadableDayTime(parsedDepartureTime) : ''}
                                                            </time>
                                                            <span>{flight.originAirport}</span>
                                                        </p>

                                                        {parsedArrivalTime && (
                                                            <div className="flex flex-col md:flex-row md:items-center text-s md:gap-64">
                                                                <p className="flex flex-row gap-4 md:gap-8">
                                                                    <time dateTime={flight.arrivalTime} className="font-bold capitalize">
                                                                        {formatReadableDayTime(parsedArrivalTime)}
                                                                    </time>
                                                                    <span>{flight.destinationAirport}</span>
                                                                </p>
                                                                {flight.flightDuration && (
                                                                    <span className="flex flex-row items-center gap-8 md:font-bold">
                                                                        <IconClock16 className="md:hidden" aria-hidden />
                                                                        {formatDuration(flight.flightDuration)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    </li>
                                                ),
                                            )}
                                        </ul>
                                    </li>
                                ))}
                            </ul>

                            <Alert type="warning" show={showUnknownStopoversWarning}>
                                Enkelte flyvninger kan ha mellomlandinger. Gå til tilbyder for å se detaljer om flyving
                            </Alert>
                        </section>

                        <div className="flex flex-col md:basis-1/3 md:border-l md:px-16 md:justify-between gap-16">
                            <section className="flex flex-col gap-8 md:justify-between">
                                <h3 className="flex flex-row gap-8 items-center t4 md:t3">
                                    Overnatting <IconHouseBed16 className="md:hidden" aria-hidden />
                                </h3>

                                <ul className="space-y-8">
                                    <li className="flex items-center gap-8">
                                        <IconUsers16 aria-hidden />
                                        <span>{paxText}</span>
                                    </li>
                                    <li className="flex items-center gap-8">
                                        <IconHouseBed16 aria-hidden />
                                        <span>{roomDescription}</span>
                                    </li>
                                    {board && (
                                        <li className="flex items-center gap-8">
                                            <IconDiner16 aria-hidden />
                                            <span>{meals[board]}</span>
                                        </li>
                                    )}
                                </ul>
                            </section>

                            <hr className="hidden md:block" />

                            <div>
                                <section>
                                    <h3 className="flex flex-row gap-8 items-center t4 md:t3">
                                        Totalpris <IconTag16 className="md:hidden" aria-hidden />
                                    </h3>
                                    <ul>
                                        <li className="text-l">{thousandSeparated(adjustedPriceTotal)} kr</li>
                                        <li>Pris per person {thousandSeparated(adjustedPricePerPerson)} kr</li>
                                    </ul>
                                </section>

                                <Button
                                    primary
                                    fullWidth
                                    href={`${contextPathPP}/ut?offerId=${offerId}&fromPage=hotel${filterNumberOfAdults ? `&numberOfAdults=${filterNumberOfAdults}` : ''}${filterAgesOfChildren?.length ? `&agesOfChildren=${filterAgesOfChildren.join(',')}` : ''}`}
                                    onClick={() => trackClickOfferHotel(offer, hotel)}
                                    data-testid={`offerLink-${offerId}`}
                                    className="mt-8"
                                    rel="noopener noreferrer nofollow"
                                    target="_blank">
                                    Gå til bestilling
                                </Button>
                            </div>
                        </div>
                    </div>
                </Expandable>
            </div>
        </article>
    );
};
